import React from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { SIGN_IN_PROVIDERS } from 'fym-shared/src/music24/signInMethods';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { Panel, PanelBody, PanelHead } from '../../../../music24/components/primitives/Panel';
import { useIsNativeEmbed } from '../../../../music24/navigation/hooks/useIsNativeEmbed';
import { signInMethodsMessages } from './signInMethodsMessages';
import { signInMethodErrorMessage } from './signInMethodError';
import { useSignInMethods } from './useSignInMethods';
import { SignInProviderRow } from './SignInProviderRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../../settingsCards.module.scss';
/* eslint-enable css-modules/no-unused-class */
import { withBrandWord } from '../../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sign-in methods card; each part uses its own slice of it */
import styles from './signInMethods.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Settings → Account → Sign-in methods: the email code on the primary address, and Apple and
 * Google with Connect (link mode) or Disconnect. design: sign-in-methods a1 (card), a2 (states),
 * a3 (confirm), a4 (phone); copies in docs/superpowers/design-reference/music24-sign-in-methods/.
 */
const SignInMethodsCardComponent: React.FC = () => {
  const intl = useIntl();
  const isNativeEmbed = useIsNativeEmbed();
  const methods = useSignInMethods({ canConnect: !isNativeEmbed });
  const { data, failure, linkedProvider } = methods;
  const providerName = (provider: 'apple' | 'google' | null): string =>
    provider === null
      ? ''
      : intl.formatMessage(provider === 'apple' ? signInMethodsMessages.apple : signInMethodsMessages.google);

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(signInMethodsMessages.title)}
        subtitle={intl.formatMessage(signInMethodsMessages.subtitle)}
      />
      {methods.isLoading && (
        <PanelBody>
          <LoadingView />
        </PanelBody>
      )}
      {!methods.isLoading && methods.error !== null && (
        <PanelBody>
          <ErrorView error={methods.error} retry={methods.refetch} />
        </PanelBody>
      )}
      {data !== undefined && (
        <>
          <div className={cards.row}>
            <span className={cards.rowIcon}>
              <FontAwesomeIcon icon={faEnvelope} />
            </span>
            <div className={cards.rowText}>
              <span className={cards.rowTitle}>{intl.formatMessage(signInMethodsMessages.emailCode)}</span>
              {data.emailCode.usable ? (
                <span className={cards.rowSub} data-clarity-mask="true">
                  {intl.formatMessage(signInMethodsMessages.emailCodeDetail, { email: data.primaryEmail })}
                </span>
              ) : (
                <span className={styles.relayNote}>
                  <FontAwesomeIcon icon={faCircleExclamation} className={styles.relayNoteIcon} />
                  <span>{intl.formatMessage(signInMethodsMessages.emailCodeRelay)}</span>
                </span>
              )}
            </div>
            {data.emailCode.usable && (
              <div className={cards.rowEnd}>
                <span className={`${cards.pill} ${cards.pillPos}`}>
                  {intl.formatMessage(signInMethodsMessages.alwaysOn)}
                </span>
              </div>
            )}
          </div>
          {SIGN_IN_PROVIDERS.map((provider) => (
            <SignInProviderRow
              key={provider}
              provider={provider}
              identity={data.identities.find((identity) => identity.provider === provider) ?? null}
              canConnect={methods.canConnectProvider(provider)}
              isReady={methods.isProviderReady(provider)}
              isConnecting={methods.busyProvider === provider}
              isDisconnecting={methods.isDisconnecting}
              onConnect={methods.connect}
              onDisconnect={methods.disconnect}
            />
          ))}
          {isNativeEmbed && (
            <PanelBody>
              <span className={cards.muted}>{withBrandWord(intl.formatMessage(signInMethodsMessages.nativeHint))}</span>
            </PanelBody>
          )}
        </>
      )}
      {(failure !== null || linkedProvider !== null) && (
        <div className={styles.notice}>
          {failure !== null ? (
            <Alert color="danger" className="mb-0" role="alert">
              {withBrandWord(
                intl.formatMessage(signInMethodErrorMessage(failure.error), {
                  provider: providerName(failure.provider),
                })
              )}
            </Alert>
          ) : (
            <Alert color="success" className="mb-0 d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faCheck} />
              <span>
                {intl.formatMessage(signInMethodsMessages.linked, { provider: providerName(linkedProvider) })}
              </span>
            </Alert>
          )}
        </div>
      )}
    </Panel>
  );
};

export const SignInMethodsCard = TypedMemo(SignInMethodsCardComponent);
